import {
  Card,
  Divider,
  Drawer,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography
} from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import {
  DeleteRounded,
  GroupRounded,
  MoreVertRounded,
  ShowChartRounded,
  StarBorderRounded,
  StarRounded,
  VisibilityRounded
} from '@material-ui/icons';
import clsx from 'clsx';
import { useState } from 'react';
import { Link as RRLink, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MAX_NUMBER_OF_BOOKMARK_PROJECTS } from '../constants';
import { Project } from '../pages/Project/projectSlice';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    fullList: {
      width: 'auto'
    },
    divider: {
      margin: theme.spacing(1, 0)
    },
    card: {
      border: '2px solid',
      borderColor: theme.palette.background.paper,
      borderRadius: 16,
      transition: '0.4s',
      '&:hover, &.Mui-focusVisible': {
        borderColor: theme.palette.primary.main
      },
      display: 'flex',
      height: '100%'
    },
    title: {
      flexGrow: 1,
      textDecoration: 'none',
      color: theme.palette.text.primary
    },
    paddingTopBottom: {
      padding: theme.spacing(1, 0)
    },
    paddingTopBottomLeft: {
      padding: theme.spacing(1, 0, 1, 1)
    },
    paddingLeft: {
      paddingLeft: theme.spacing(1.5)
    },
    paddingTopBottomRight: {
      padding: theme.spacing(1, 1, 1, 0)
    }
  })
);

interface ProjectCardProps {
  renderProjects: Project[];
  bookmarkProjectIds?: number[];
  handleBookmark?: (project: Project, isBookmark: boolean) => void;
}

export default function ProjectCard({
  renderProjects,
  bookmarkProjectIds = [],
  handleBookmark
}: ProjectCardProps) {
  const classes = useStyles();
  const { t } = useTranslation();

  const [openOption, setOpenOption] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  function toggleDrawer(open: boolean, project?: Project) {
    return (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event.type === 'keydown' &&
        ((event as React.KeyboardEvent).key === 'Tab' ||
          (event as React.KeyboardEvent).key === 'Shift')
      ) {
        return;
      }
      if (open && project) {
        setSelectedProject(project);
      }
      setOpenOption(open);
    };
  }

  return (
    <div>
      <Drawer anchor="bottom" open={openOption} onClose={toggleDrawer(false)}>
        <div
          className={classes.fullList}
          role="presentation"
          onClick={toggleDrawer(false)}
          onKeyDown={toggleDrawer(false)}
        >
          <List>
            <ListItem>
              <ListItemText
                primary={`${selectedProject?.projectNo} : ${selectedProject?.name}`}
              />
            </ListItem>
            <Divider className={classes.divider} />
            <ListItem
              component={NavLink}
              to={`/projects/${selectedProject?.id}`}
              button
            >
              <ListItemIcon>
                <VisibilityRounded />
              </ListItemIcon>
              <ListItemText primary={t('projectCard.viewProjectDetails')} />
            </ListItem>
            <ListItem
              component={NavLink}
              to={`/projects/${selectedProject?.id}/dashboards`}
              button
            >
              <ListItemIcon>
                <ShowChartRounded />
              </ListItemIcon>
              <ListItemText primary={t('projectCard.viewProjectDashboard')} />
            </ListItem>
            <ListItem
              component={NavLink}
              to={`/projects/${selectedProject?.id}/members`}
              button
            >
              <ListItemIcon>
                <GroupRounded />
              </ListItemIcon>
              <ListItemText primary={t('projectCard.manageMembers')} />
            </ListItem>
            <Divider className={classes.divider} />
            <ListItem button disabled>
              <ListItemIcon>
                <DeleteRounded />
              </ListItemIcon>
              <ListItemText
                primary={t('projectCard.delete')}
                secondary={t('projectCard.functionNotAvailable')}
              />
            </ListItem>
          </List>
        </div>
      </Drawer>
      <Grid container spacing={1} justify="flex-start">
        {renderProjects?.map((project) => {
          const isBookmark: boolean = bookmarkProjectIds
            ? bookmarkProjectIds.some((bookmark) => bookmark === project.id)
            : false;
          let title;
          if (isBookmark) {
            title = t('projectCard.removeBookmark');
          } else if (
            bookmarkProjectIds.length < MAX_NUMBER_OF_BOOKMARK_PROJECTS
          ) {
            title = t('projectCard.bookmark');
          } else {
            title = t('projectCard.maxBookmarkLimit', { count: MAX_NUMBER_OF_BOOKMARK_PROJECTS });
          }
          return (
            <Grid item xs={12} sm={6} md={4} key={project.id}>
              <Card className={classes.card}>
                {handleBookmark && (
                  <div className={classes.paddingTopBottomLeft}>
                    <Tooltip title={title} arrow>
                      <span>
                        <IconButton
                          size="small"
                          edge="start"
                          onClick={() => {
                            handleBookmark(project, isBookmark);
                          }}
                          disabled={
                            !isBookmark &&
                            bookmarkProjectIds.length >=
                              MAX_NUMBER_OF_BOOKMARK_PROJECTS
                          }
                        >
                          {isBookmark ? (
                            <StarRounded color="primary" />
                          ) : (
                            <StarBorderRounded />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </div>
                )}
                <RRLink
                  className={clsx(classes.title, classes.paddingTopBottom, {
                    [classes.paddingLeft]: !Boolean(handleBookmark)
                  })}
                  to={`/projects/${project.id}`}
                >
                  <Typography variant="h6">{project?.projectNo}</Typography>
                  <Typography variant="body2">{project?.name}</Typography>
                </RRLink>
                <div className={classes.paddingTopBottomRight}>
                  <IconButton
                    size="small"
                    onClick={toggleDrawer(true, project)}
                  >
                    <MoreVertRounded />
                  </IconButton>
                </div>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </div>
  );
}
